'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useUi } from '@weareconceptstudio/core';
import { useCart } from '@weareconceptstudio/cart';
import { useAccountContext, useAddressContext } from '@weareconceptstudio/account';
import { getNavigationIntent, storeNavigationIntent } from '@/helpers/fulfillmentNavigation';
import {
	getHasFulfillmentSelection,
	readPendingAddress,
	showFulfillmentRequiredPopup,
} from '@/helpers/fulfillmentGuard';
import { useNavigateWithPageLoading } from '@/hooks/usePageLoading';

export const useFulfillmentCartGuard = ({ enableRouteGuard = false } = {}) => {
	const pathname = usePathname();
	const navigateWithLoading = useNavigateWithPageLoading();
	const { openPopup, closePopup } = useUi();
	const { checkoutData } = useCart();
	const { fulfillment, useUser } = useAccountContext();
	const { isLoggedIn } = useUser();
	const { selectedAddresses, hasAddressType } = useAddressContext();
	const [pendingAddress, setPendingAddress] = useState(readPendingAddress);
	const redirectedRef = useRef(false);
	// Track whether address data has been fetched from the API.
	// selectedAddresses starts as null (before the API call completes).
	// Once it becomes non-null (even an empty object), the fetch has settled.
	const addressesResolvedRef = useRef(false);
	const [addressesReady, setAddressesReady] = useState(false);

	useEffect(() => {
		const handler = (event) => setPendingAddress(event.detail || null);
		window.addEventListener('pendingAddressSaved', handler);
		return () => window.removeEventListener('pendingAddressSaved', handler);
	}, []);

	useEffect(() => {
		if (addressesResolvedRef.current) return;
		// Not logged in — no address fetch happens, ready immediately
		if (!isLoggedIn) {
			addressesResolvedRef.current = true;
			setAddressesReady(true);
			return;
		}
		// For takeaway, selectedAddresses stays null — ready as soon as checkoutData has fulfillmentType
		const isTakeaway = checkoutData?.fulfillmentType === 'takeaway';
		if (isTakeaway) {
			addressesResolvedRef.current = true;
			setAddressesReady(true);
			return;
		}
		// For delivery, wait until selectedAddresses is populated (API fetch done)
		if (selectedAddresses !== null) {
			addressesResolvedRef.current = true;
			setAddressesReady(true);
		}
	}, [isLoggedIn, checkoutData?.fulfillmentType, selectedAddresses]);

	const hasFulfillmentSelection = useMemo(
		() =>
			getHasFulfillmentSelection({
				checkoutData,
				selectedAddresses,
				hasAddressType,
				isLoggedIn,
				branches: fulfillment?.branches || [],
				pendingAddress,
			}),
		[checkoutData, selectedAddresses, hasAddressType, isLoggedIn, fulfillment?.branches, pendingAddress]
	);

	const isCartOrCheckout = pathname.includes('/cart/') || pathname.includes('/checkout/');
	const routeIntent = pathname.includes('/checkout/') ? 'checkout' : 'cart';

	useEffect(() => {
		if (!enableRouteGuard || !addressesReady) return;

		if (!isCartOrCheckout) {
			redirectedRef.current = false;
			return;
		}

		if (hasFulfillmentSelection || redirectedRef.current) return;

		redirectedRef.current = true;
		storeNavigationIntent(routeIntent);
		navigateWithLoading('menu', { replace: true });
		showFulfillmentRequiredPopup(openPopup, closePopup, {
			initialTab: checkoutData?.fulfillmentType === 'takeaway' ? 'takeaway' : 'delivery',
			redirectAfterConfirm: routeIntent,
		});
	}, [
		checkoutData?.fulfillmentType,
		closePopup,
		enableRouteGuard,
		addressesReady,
		hasFulfillmentSelection,
		isCartOrCheckout,
		navigateWithLoading,
		openPopup,
		routeIntent,
	]);

	const blockNavigation = useCallback(
		(event, url) => {
			if (hasFulfillmentSelection) return false;

			event.preventDefault();
			event.stopPropagation();
			showFulfillmentRequiredPopup(openPopup, closePopup, {
				initialTab: checkoutData?.fulfillmentType === 'takeaway' ? 'takeaway' : 'delivery',
				redirectAfterConfirm: getNavigationIntent(url) || 'cart',
			});
			return true;
		},
		[checkoutData?.fulfillmentType, closePopup, hasFulfillmentSelection, openPopup]
	);

	return { hasFulfillmentSelection, blockNavigation };
};
